import axios, { AxiosError } from "axios";
import { IAxiosRequestConfig } from "./ApiModels";
import { ApiMessageBuilder, IApiError, IApiErrorLabels } from "./ApiMessageBuilder";
import { Authentication, LogOutReason } from "../Authentication";
import type { Core } from "../Core";

export class ApiClient implements IApiClient
{
    private readonly authentication: Authentication;
    private readonly core: Core;

    constructor(authentication: Authentication, core: Core)
    {
        this.authentication = authentication;
        this.core = core;
    }

    public initialise(): void
    {
        axios.interceptors.request.use(request =>
        {
            const options = this.core.apiClientOptions;
            request.headers["App-Version"] = options?.appVersionHeader ?? "";

            const authToken = this.authentication.getToken();
            if (authToken)
            {
                request.headers["Authorization"] = `bearer ${authToken}`;
            }

            const currentLang = this.core.getLanguage();
            if (currentLang != null && currentLang !== "")
            {
                request.headers["Accept-Language"] = currentLang;
            }

            return request;
        });

        axios.interceptors.response.use(
            response => response,
            (error: AxiosError) => this.handleResponseError(error)
        );
    }

    private handleResponseError(error: AxiosError): Promise<unknown>
    {
        const options = this.core.apiClientOptions;
        const config = error.config as IAxiosRequestConfig | undefined;
        const statusCode = error?.response?.status;
        const isPublic = (options?.isPublicRoute() ?? false);
        const isLoggingIn = (options?.isLoggingIn() ?? false);
        const suppressErrorPopup = (config?.suppressErrorPopup ?? false);

        if (statusCode === 401 && !isPublic)
        {
            // log out if we get another 401 after refreshing the access token (to guard against an infinite loop)
            if (!config || config.tokenRefreshed == true)
            {
                this.authentication.logOut(LogOutReason.SessionExpired);
                return Promise.reject(error);
            }

            return this.authentication
                .refreshToken()
                .then(token =>
                {
                    config.tokenRefreshed = true;
                    config.headers["Authorization"] = `bearer ${token}`;
                    return axios(config);
                })
                .catch(() =>
                {
                    this.authentication.logOut(LogOutReason.SessionExpired);
                    return Promise.reject(error);
                });
        }
        else if (statusCode === 403 && !isPublic)
        {
            if (!suppressErrorPopup)
            {
                this.raiseApiError(error);
            }
            return Promise.reject(error);
        }
        else if (statusCode === 403 && isLoggingIn)
        {
            this.authentication.logOut(LogOutReason.AccountIncomplete);
            return Promise.reject(error);
        }
        else
        {
            if (!suppressErrorPopup)
            {
                this.raiseApiError(error);
            }
            return Promise.reject(error);
        }
    }

    private raiseApiError(error: AxiosError): void
    {
        const options = this.core.apiClientOptions;
        if (!options)
        {
            return;
        }

        const messageBuilder = new ApiMessageBuilder(options.labels, options.getApiMessage, this.core.getLanguage());
        const apiError = messageBuilder.getApiError(error);
        options.onApiError(apiError);
    }
}

export interface IApiClient
{
    initialise(): void;
}

export interface IApiClientOptions
{
    appVersionHeader: string;
    isPublicRoute: () => boolean;
    isLoggingIn: () => boolean;
    onApiError: (error: IApiError) => void;
    labels: IApiErrorLabels;
    getApiMessage: (key: string) => string;
}
